import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BlogCard } from "@/components/BlogCard";
import { CATEGORIES, getCategory, getPostsByCategory } from "@/data/blog";
import { ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/blog/$category/")({
  head: ({ params }) => {
    const cat = getCategory(params.category);
    const url = `https://legaldecoder.io/blog/${params.category}`;
    return {
      meta: [
        { title: cat ? `${cat.title} — legaldecoder` : "Category — legaldecoder" },
        { name: "description", content: cat?.description ?? "Lease guides and resources." },
        { property: "og:title", content: cat?.title ?? "Lease Guides" },
        { property: "og:description", content: cat?.description ?? "Lease guides and resources." },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: CategoryPage,
  notFoundComponent: NotFound,
});

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Category not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-primary-glow hover:underline">
          Back to blog
        </Link>
      </div>
    </div>
  );
}

function CategoryPage() {
  const { category } = Route.useParams();
  const cat = getCategory(category);
  const posts = getPostsByCategory(category);

  if (!cat) return <NotFound />;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-border">
          <div className="absolute inset-0 bg-gradient-glow" aria-hidden />
          <div className="relative mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
            <Link
              to="/blog"
              className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> All topics
            </Link>
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-wider text-primary-glow">
                Topic
              </span>
              <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">{cat.title}</h1>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                {cat.description}
              </p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>

            <div className="mt-16 border-t border-border pt-10">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Other topics
              </h3>
              <div className="flex flex-wrap gap-3">
                {CATEGORIES.filter((c) => c.slug !== cat.slug).map((c) => (
                  <Link
                    key={c.slug}
                    to="/blog/$category"
                    params={{ category: c.slug }}
                    className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-primary-glow hover:text-primary-glow"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}
